/**
 * Pipeline telemetry — one durable record per first-pass run.
 *
 * WHY THIS EXISTS
 * ---------------
 * Every timing bug in this app has been diagnosed by reconstructing, after the
 * fact, what each stage did to the boundaries. Until now that reconstruction was
 * guesswork from the final segment table: you could not tell a bad Gemini draw
 * from a refinement that bailed, because both leave the same kind of wreckage.
 * The single most useful missing datum was `refineBoundaries`' guard — when it
 * returns the model's timings untouched, the whole alignment stack is a no-op
 * and nothing anywhere said so.
 *
 * So: a run log captures the audio as decoded, the model's RAW response before
 * anything touches it, a boundary snapshot after every stage, and the alignment
 * score of each. It is written to IndexedDB and downloadable from the task card.
 *
 * DESIGN
 * ------
 * Module-level "current run" rather than a logger threaded through every
 * signature. `gemini.ts` records its own calls without knowing who is asking,
 * and the stage functions stay pure. Runs do not nest (`firstPass` holds an
 * in-flight lock per task and the app is single-threaded through the pipeline),
 * so one active slot is enough; a second `beginRun` simply replaces the first.
 *
 * Nothing here may ever throw into the pipeline. Telemetry that can fail a run
 * is worse than no telemetry.
 */

import { putRunLog } from '../db';
import { round3 } from '../lib/num';
import type { RawSegment } from '../types';

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

/**
 * The alignment score as it is STORED, declared structurally on purpose.
 *
 * The scorer itself lives in `legacy/alignmentScore.ts` — it is reached only
 * from the legacy branch — and this module is core. Importing the type from
 * there, even as `import type`, pointed a core module at the quarantine and
 * closed a four-module import cycle (gemini → pipelineLog → alignmentScore →
 * burstResegment → gemini). Every field is a plain number, so declaring the
 * shape here costs nothing and `AlignmentScore` stays assignable to it.
 *
 * KEEP IN STEP with `legacy/alignmentScore.ts`'s `AlignmentScore`: this is a
 * stored shape, and the compiler will only catch a divergence at the one
 * assignment site in `legacy/legacyPipeline.ts`.
 */
export interface AlignmentScoreLog {
  /** Overall 0..1, higher is better. */
  score: number;
  /** Fraction of labelled time landing on detected speech, 0..1. */
  spanOnSpeech: number;
  /** Fraction of detected speech covered by some segment, 0..1. */
  speechRecall: number;
  /** Segments whose duration is impossible for their label. */
  implausibleCount: number;
  /** {@link implausibleCount} over the segment count, 0..1. */
  implausibleFrac: number;
  /** Speech bursts with no segment over them. */
  emptyBursts: number;
  /** Total bursts found. */
  totalBursts: number;
  /** Seconds of detected speech that no segment covers. */
  unlabelledSpeechSec: number;
  /** Seconds of detected speech in total. */
  totalSpeechSec: number;
  /** End of the last segment, seconds. */
  lastEndSec: number;
  /** Real audio duration, seconds. */
  durationSec: number;
  /** `lastEndSec - durationSec`; negative means the draw stops short. */
  tailErrorSec: number;
  /** Segment count scored. */
  segmentCount: number;
}

/** The source file exactly as the browser received it. */
export interface SourceInfo {
  name: string;
  /** MIME type the browser assigned. `''` when the OS gave none. */
  type: string;
  sizeBytes: number;
}

/** What the Web Audio decoder made of it — the "mic info" question. */
export interface DecodedInfo {
  sampleRate: number;
  channels: number;
  durationSec: number;
  lengthSamples: number;
}

/** What actually went up the wire to Gemini. */
export interface UploadInfo {
  mimeType: string;
  sampleRate: number;
  channels: number;
  bitsPerSample: number;
  sizeBytes: number;
  base64Chars?: number;
}

/** One Gemini request. `raw` is only kept for the first pass — it is large. */
export interface GeminiCallLog {
  /*
   * `readCut` and `alternatives` used to be logged as `clipQa`, so three
   * different stages were indistinguishable in the run log. The stored field
   * is a plain string: no DB_VERSION change, and old logs still parse (they
   * simply say `clipQa` where a new one would say `readCut`).
   */
  kind: 'firstPass' | 'clipQa' | 'readCut' | 'alternatives' | 'retimeRegion' | 'gapFill' | 'verbatim';
  startedAt: number;
  ms: number;
  ok: boolean;
  finishReason?: string;
  error?: string;
  /** Verbatim response text, first pass only. The untouched timestamps. */
  raw?: string;
  /** Attempt number for the first pass, 1-based. */
  attempt?: number;
}

/** Boundaries as they stood at the end of a stage. */
export interface StageSnapshot {
  stage: string;
  segmentCount: number;
  /** `[startSec, endSec, heard]` per segment — compact on purpose. */
  segments: Array<[number, number, string]>;
  score?: AlignmentScoreLog;
  /** Mean |start - previousStageStart| over the segments both stages share. */
  meanDriftSec?: number;
  maxDriftSec?: number;
  note?: string;
}

/** One first-pass draw and how it scored. */
export interface DrawLog {
  attempt: number;
  rawSegmentCount: number;
  /** Rejected outright by {@link drawTimingFault} without being refined. */
  timingFault?: string;
  rawScore?: AlignmentScoreLog;
  refinedScore?: AlignmentScoreLog;
  refine?: RefineLog;
  chosen: boolean;
}

/** What `refineBoundaries` decided. `applied: false` is the prime suspect. */
export interface RefineLog {
  applied: boolean;
  reason?: string;
  meanShiftSec: number;
  maxShiftSec: number;
  coverageBefore: number;
  coverageAfter: number;
  /** Alignment score after the global warp alone, to isolate the DTW's value. */
  warpOnlyScore?: number;
  /** False means the banded DTW found no path and the warp was used alone. */
  dtwApplied?: boolean;
  /** What stage 5 (onset anchoring) did. */
  onsets?: OnsetStageLog;
}

/**
 * Stage 5, per burst.
 *
 * The per-burst rows are the point. A burst that was left alone and a burst that
 * was re-cut look identical in the final segment table, and the difference —
 * "there were only two onsets for five labels" versus "the local score dropped
 * so it was reverted" — is the whole diagnosis when a clip still sounds wrong.
 */
export interface OnsetStageLog {
  /** Alignment score before stage 5 and after it. */
  scoreBefore?: number;
  scoreAfter?: number;
  burstsEligible: number;
  burstsApplied: number;
  segmentsMoved: number;
  meanShiftSec: number;
  maxShiftSec: number;
  /** One row per burst; see `BurstAlignLog` in services/legacy/onsetAlign.ts. */
  bursts: Array<{
    burstIndex: number;
    startSec: number;
    endSec: number;
    segmentCount: number;
    onsetCount: number;
    candidateCount: number;
    cost?: number;
    scoreBefore: number;
    scoreAfter: number;
    applied: boolean;
    note?: string;
  }>;
}

/**
 * Burst-first GROUP segmentation, per burst. See `services/legacy/burstGroups.ts`.
 *
 * The per-burst rows are the whole diagnosis, exactly as they are for the onset
 * stage: "seven words went into this burst and came out as two segments because
 * only one onset in it cleared 0.85" and "this boundary was invented because the
 * three-second cap demanded one" look identical in the final table and mean
 * completely different things. `splits` is the honest record of which boundaries
 * the audio offered and which the pipeline had to make up.
 */
export interface GroupingLog {
  segmentsBefore: number;
  segmentsAfter: number;
  /** Config in force, so a log can be read without the source next to it. */
  maxGroupWords: number;
  maxGroupSec: number;
  minGroupSec: number;
  splitStrength: number;
  multiWordGroups: number;
  wordsFused: number;
  emptyBursts: number;
  confidentSplits: number;
  forcedSplits: number;
  capViolations: number;
  widenedToFloor: number;
  /** Fraction of boundaries on silence or a confident onset. 1 is the target. */
  boundaryEvidenceFrac: number;
  /** One row per burst; see `BurstGroupLog` in services/legacy/burstGroups.ts. */
  bursts: Array<{
    burstIndex: number;
    startSec: number;
    endSec: number;
    wordsIn: number;
    segmentsOut: number;
    confidentOnsets: number;
    splits: Array<{
      timeSec: number;
      strength: number;
      source: 'flux' | 'dip' | 'even';
      confident: boolean;
      forced: boolean;
    }>;
    labels: string[];
    capViolations: number;
    note?: string;
  }>;
}

/** How much of the audible speech any label at all covers. See `gapFill.ts`. */
export interface CompletenessSnapshot {
  /** Fraction of detected speech time some segment covers, 0..1. */
  speechCoveredFrac: number;
  /** Seconds of detected speech no segment covers. */
  unlabelledSpeechSec: number;
  /** Total detected speech, seconds. */
  totalSpeechSec: number;
  /** Unlabelled runs holding at least 180 ms of solid speech. */
  gapCount: number;
  /** Seconds of speech in those runs. */
  gapSec: number;
  segmentCount: number;
}

export interface RunLog {
  id: string;
  taskId: string;
  taskName: string;
  startedAt: number;
  endedAt?: number;
  totalMs?: number;
  outcome: 'running' | 'ok' | 'error';
  error?: string;

  source?: SourceInfo;
  decoded?: DecodedInfo;
  upload?: UploadInfo;

  /*
   * ================================================================
   * WHICH PIPELINE WROTE THIS?
   *
   * `draws`, `refine`/`onsets` (inside a DrawLog), `grouping`, `qa`,
   * `resegment`, `gapFill` and `completeness` are populated by the LEGACY
   * branch ONLY. An aligned run leaves every one of them empty or absent, so
   * a perfectly good aligned run reads like a legacy run that failed before
   * it started. There is no discriminant field: the honest fix is to split
   * this into a shared envelope plus `legacy?`/`aligned?` detail, which
   * touches a STORED shape and is therefore deferred (follow-up F5).
   *
   * Until then: an empty `draws` array means "the aligned path ran", not
   * "the model was never asked".
   * ================================================================
   */

  /** Every draw attempted, including the ones thrown away. LEGACY ONLY. */
  draws: DrawLog[];
  /** Which attempt's segments were carried forward. */
  chosenAttempt?: number;
  /** Why a re-roll happened, when it did. */
  retryReason?: string;

  stages: StageSnapshot[];
  geminiCalls: GeminiCallLog[];
  apiCallCount: number;

  /** `ClipQaStats`, structurally — kept loose so QA can grow fields freely. */
  qa?: Record<string, unknown>;
  /** `ResegmentStats`, same reasoning. */
  resegment?: Record<string, unknown>;
  /** `GapFillStats`, same reasoning. */
  gapFill?: Record<string, unknown>;

  /** What burst-first grouping decided. See {@link GroupingLog}. */
  grouping?: GroupingLog;

  /**
   * LABEL COMPLETENESS, before and after the gap-fill stage.
   *
   * The metric the user's "it drifts" complaint actually lives in. Every other
   * number in this log measures whether the boundaries sit on speech; none of
   * them can see that a word was never listed, and a missing word slides every
   * later label onto the wrong audio — indistinguishable, by ear, from a timing
   * error. Measured on the calibration recording: 3.41 s of detected speech with
   * no label over it across 6 gaps of 180 ms or more.
   */
  completeness?: {
    before: CompletenessSnapshot;
    after?: CompletenessSnapshot;
  };
  /** Per-segment QA verdicts, final state. */
  qaVerdicts?: Array<{ index: number; heard: string; confidence: number; flagged: boolean }>;

  /** Free-form breadcrumbs for anything that does not deserve a field. */
  notes: string[];

  /**
   * Another run was recording when this one started (or started while this one
   * was still going). Gemini calls are attributed to whichever run began most
   * recently, so on a flagged log the call list and counts are unreliable —
   * boundaries and scores, which are recorded by the owning run directly, are
   * still sound.
   */
  concurrent?: boolean;
}

/* ------------------------------------------------------------------ */
/* The active run                                                      */
/* ------------------------------------------------------------------ */

let active: RunLog | null = null;

/*
 * `currentRun()` was here and had no callers: every reader goes through
 * `patchRun`/`note`/`snapshotStage`, which is the point of the module-level
 * slot. Deleted rather than left as an invitation to read the log mid-run.
 */

export function beginRun(taskId: string, taskName: string): RunLog {
  /*
   * One active slot, so `gemini.ts` can record calls without being told who is
   * asking. `runFirstPass` holds a per-TASK lock, which does not stop two
   * different tasks from running at once — and then the second `beginRun` would
   * quietly orphan the first run's log. Flag both instead of pretending: an
   * obviously suspect log beats a plausible wrong one.
   */
  const displaced = active;
  if (displaced) {
    displaced.concurrent = true;
    console.warn(
      `[lableforge] pipeline log: "${taskName}" started while "${displaced.taskName}" was still recording; ` +
        'Gemini call attribution for both runs is unreliable.',
    );
  }
  active = {
    id: `${taskId}-${Date.now()}`,
    taskId,
    taskName,
    startedAt: Date.now(),
    outcome: 'running',
    draws: [],
    stages: [],
    geminiCalls: [],
    apiCallCount: 0,
    notes: [],
    concurrent: displaced ? true : undefined,
  };
  return active;
}

/** Mutate the active run. No-op when nothing is being recorded. */
export function patchRun(patch: Partial<RunLog>): void {
  if (active) Object.assign(active, patch);
}

export function note(message: string): void {
  active?.notes.push(`${((Date.now() - active.startedAt) / 1000).toFixed(1)}s ${message}`);
}

/**
 * Record a Gemini call. Called from `gemini.ts`, which has no idea a run is in
 * progress — that is the point.
 */
export function recordGeminiCall(call: GeminiCallLog): void {
  if (!active) return;
  active.apiCallCount++;
  // Clip QA issues one call per clip; keeping every one turns the log into a
  // megabyte of noise. The first pass is the interesting one and is always kept.
  if (call.kind === 'clipQa' && active.geminiCalls.filter((c) => c.kind === 'clipQa').length >= 40) {
    return;
  }
  active.geminiCalls.push(call);
}

/**
 * Snapshot the boundaries at the end of a stage, with drift against the
 * previous snapshot so a reader can see which stage moved what.
 */
export function snapshotStage(
  stage: string,
  segments: readonly RawSegment[],
  extra: { score?: AlignmentScoreLog; note?: string } = {},
): void {
  if (!active) return;
  const prev = active.stages[active.stages.length - 1];
  const snap: StageSnapshot = {
    stage,
    segmentCount: segments.length,
    segments: segments.map((s) => [round3(s.startSec), round3(s.endSec), s.heard]),
    score: extra.score,
    note: extra.note,
  };
  if (prev) {
    // Only meaningful while the stages agree on how many segments there are;
    // QA merges and re-segmentation legitimately change the count.
    const n = Math.min(prev.segments.length, snap.segments.length);
    if (n > 0 && prev.segments.length === snap.segments.length) {
      let sum = 0;
      let max = 0;
      for (let i = 0; i < n; i++) {
        const d = Math.abs(snap.segments[i][0] - prev.segments[i][0]);
        sum += d;
        if (d > max) max = d;
      }
      snap.meanDriftSec = round3(sum / n);
      snap.maxDriftSec = round3(max);
    }
  }
  active.stages.push(snap);
}

/** Finish, persist and print. Never throws. */
export async function endRun(outcome: 'ok' | 'error', error?: string): Promise<RunLog | null> {
  const run = active;
  active = null;
  if (!run) return null;
  run.endedAt = Date.now();
  run.totalMs = run.endedAt - run.startedAt;
  run.outcome = outcome;
  if (error) run.error = error;
  printSummary(run);
  try {
    await putRunLog(run);
  } catch (err) {
    console.warn('[lableforge] could not save the pipeline log:', err);
  }
  return run;
}

/* ------------------------------------------------------------------ */
/* Console summary                                                     */
/* ------------------------------------------------------------------ */

function pct(n: number | undefined): string {
  return n === undefined ? '--' : `${Math.round(n * 100)}%`;
}

/** Compact, greppable, one group per run. */
export function printSummary(run: RunLog): void {
  const tag = `[lableforge] run ${run.taskName} · ${run.outcome} · ${((run.totalMs ?? 0) / 1000).toFixed(1)}s · ${run.apiCallCount} API calls`;
  /* eslint-disable no-console */
  console.groupCollapsed(tag);
  if (run.source && run.decoded) {
    console.log(
      `source: ${run.source.name} ${run.source.type || '(no type)'} ${(run.source.sizeBytes / 1024).toFixed(0)} KB` +
        ` -> decoded ${run.decoded.sampleRate} Hz, ${run.decoded.channels} ch, ${run.decoded.durationSec.toFixed(2)}s`,
    );
  }
  if (run.upload) {
    console.log(
      `upload: ${run.upload.mimeType} ${run.upload.sampleRate} Hz ${run.upload.channels} ch ${run.upload.bitsPerSample}-bit, ${(run.upload.sizeBytes / 1024).toFixed(0)} KB`,
    );
  }
  for (const d of run.draws) {
    const bits = [`draw ${d.attempt}`, `${d.rawSegmentCount} segs`];
    if (d.timingFault) bits.push(`REJECTED: ${d.timingFault}`);
    if (d.refine) bits.push(d.refine.applied ? 'refine applied' : `refine BAILED (${d.refine.reason})`);
    if (d.refinedScore) {
      bits.push(
        `score ${d.refinedScore.score.toFixed(2)} (span ${pct(d.refinedScore.spanOnSpeech)}, recall ${pct(d.refinedScore.speechRecall)}, ${d.refinedScore.implausibleCount} impossible, ${d.refinedScore.emptyBursts}/${d.refinedScore.totalBursts} empty bursts)`,
      );
    }
    if (d.chosen) bits.push('<- KEPT');
    console.log(bits.join(' · '));
    const o = d.refine?.onsets;
    if (o) {
      console.log(
        `  onsets: ${o.burstsApplied}/${o.burstsEligible} bursts re-cut · ${o.segmentsMoved} boundaries moved ` +
          `(mean ${o.meanShiftSec.toFixed(3)}s, max ${o.maxShiftSec.toFixed(3)}s)` +
          (o.scoreBefore !== undefined && o.scoreAfter !== undefined
            ? ` · score ${o.scoreBefore.toFixed(2)} -> ${o.scoreAfter.toFixed(2)}`
            : ''),
      );
      for (const b of o.bursts) {
        if (b.applied || b.segmentCount < 2) continue;
        console.log(
          `    burst ${b.startSec.toFixed(2)}-${b.endSec.toFixed(2)} NOT re-cut: ` +
            `${b.segmentCount} segs, ${b.onsetCount} onsets${b.note ? ` · ${b.note}` : ''}`,
        );
      }
    }
  }
  if (run.retryReason) console.log(`retry: ${run.retryReason}`);
  if (run.grouping) {
    const g = run.grouping;
    console.log(
      `grouping: ${g.segmentsBefore} words -> ${g.segmentsAfter} segments · ` +
        `${g.multiWordGroups} multi-word groups (${g.wordsFused} words fused) · ` +
        `${g.confidentSplits} confident splits, ${g.forcedSplits} forced · ` +
        `boundaries on evidence ${pct(g.boundaryEvidenceFrac)} · ${g.emptyBursts} bursts with no labels`,
    );
    for (const b of g.bursts) {
      if (b.wordsIn === b.segmentsOut && !b.note) continue;
      console.log(
        `  burst ${b.startSec.toFixed(2)}-${b.endSec.toFixed(2)}: ${b.wordsIn} words -> ${b.segmentsOut} segs ` +
          `(${b.confidentOnsets} confident onsets)${b.note ? ` · ${b.note}` : ''}` +
          (b.labels.length ? ` · ${b.labels.join(' | ')}` : ''),
      );
    }
  }
  for (const s of run.stages) {
    const drift = s.meanDriftSec === undefined ? '' : ` · moved mean ${s.meanDriftSec.toFixed(3)}s max ${s.maxDriftSec?.toFixed(3)}s`;
    const sc = s.score ? ` · score ${s.score.score.toFixed(2)}` : '';
    console.log(`stage ${s.stage}: ${s.segmentCount} segs${drift}${sc}${s.note ? ` · ${s.note}` : ''}`);
  }
  if (run.completeness) {
    const { before: b, after: a } = run.completeness;
    console.log(
      `completeness: ${pct(b.speechCoveredFrac)} -> ${pct(a?.speechCoveredFrac)} of speech labelled · ` +
        `${b.unlabelledSpeechSec.toFixed(2)}s -> ${(a?.unlabelledSpeechSec ?? b.unlabelledSpeechSec).toFixed(2)}s unlabelled · ` +
        `${b.gapCount} -> ${a?.gapCount ?? b.gapCount} gaps >= 180ms`,
    );
  }
  if (run.gapFill) console.log('gap fill:', run.gapFill);
  if (run.qa) console.log('qa:', run.qa);
  if (run.notes.length) console.log('notes:', run.notes);
  console.log('full log object:', run);
  console.groupEnd();
  /* eslint-enable no-console */
}
